
import React from "react";
import { X, Trash2, Code, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface SkillsListProps {
  filteredSkills: string[];
  handleRemoveSkill: (skill: string) => void;
  searchQuery: string;
}

export const SkillsList: React.FC<SkillsListProps> = ({
  filteredSkills,
  handleRemoveSkill,
  searchQuery,
}) => {
  // Group skills by first letter for better organization
  const groupedSkills = filteredSkills && filteredSkills.length > 0 ? filteredSkills?.reduce((acc, skill) => {
    const firstLetter = skill.charAt(0).toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(skill);
    return acc;
  }, {} as Record<string, string[]>) : {};

  // Sort the groups alphabetically
  const sortedGroups = Object.keys(groupedSkills).sort();

  if (filteredSkills?.length === 0) {
    return (
      <div className="text-center py-8 bg-gradient-to-br from-blue-50/50 to-emerald-50/50 rounded-lg border border-dashed border-blue-200 p-6">
        {searchQuery ? (
          <div className="text-gray-500">
            <Code className="h-12 w-12 mx-auto text-blue-300 mb-3" />
            <p className="text-sm">No skills matching "<span className="font-medium text-blue-600">{searchQuery}</span>" found.</p>
          </div>
        ) : (
          <div className="text-gray-500">
            <Code className="h-12 w-12 mx-auto text-blue-300 mb-3" />
            <p className="text-sm">No skills added yet.</p>
            <p className="text-xs mt-2">Click the "Add Skill" button to add your first skill.</p>
          </div>
        )}
      </div>
    );
  }

  if (searchQuery) {
    // When searching, display a flat list
    return (
      <div className="flex flex-wrap gap-2 animate-fade-in py-2">
        {filteredSkills.map((skill) => (
          <Badge
            key={skill}
            variant="secondary"
            className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-50 to-emerald-50 hover:from-blue-100 hover:to-emerald-100 group relative border border-blue-100 shadow-sm hover:shadow transition-all"
          >
            {skill}
            <Button
              size="sm"
              variant="ghost"
              className="h-6 w-6 p-0 ml-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:bg-red-50 hover:text-red-600"
              onClick={() => handleRemoveSkill(skill)}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
      </div>
    );
  }

  // Regular grouped display
  return (
    <div className="space-y-6">
      {sortedGroups.map((letter) => (
        <div key={letter} className="space-y-2 animate-fade-in" style={{ animationDelay: "0.1s" }}>
          <div className="flex items-center gap-2">
            <div className="h-6 w-6 rounded-full bg-gradient-to-r from-blue-100 to-emerald-100 flex items-center justify-center text-xs font-medium text-blue-600">
              {letter}
            </div>
            <div className="h-px flex-1 bg-gradient-to-r from-blue-100 to-emerald-100"></div>
          </div>
          <div className="flex flex-wrap gap-2 pl-2">
            {groupedSkills[letter].map((skill) => (
              <Badge
                key={skill}
                variant="success"
                className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-50 to-emerald-50 hover:from-blue-100 hover:to-emerald-100 group relative border border-blue-100 shadow-sm hover:shadow transition-all"
              >
                {skill}
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 w-6 p-0 ml-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:bg-red-50 hover:text-red-600"
                  onClick={() => handleRemoveSkill(skill)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </Badge>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};
